import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface MetricCardProps {
  label: string;
  value: string | number;
  unit?: string;
  trend?: 'up' | 'down' | 'flat';
  trendValue?: string;
  trendPositive?: boolean;
  icon?: React.ReactNode;
  accent?: 'blue' | 'green' | 'red' | 'amber' | 'purple' | 'cyan';
  alert?: boolean;
  subtitle?: string;
}

const accentColors: Record<string, string> = {
  blue: '#4f7ef8',
  green: '#22c55e',
  red: '#f43f5e',
  amber: '#f59e0b',
  purple: '#a78bfa',
  cyan: '#22d3ee',
};

export default function MetricCard({
  label,
  value,
  unit,
  trend,
  trendValue,
  trendPositive,
  icon,
  accent = 'blue',
  alert = false,
  subtitle,
}: MetricCardProps) {
  const color = accentColors[accent];

  return (
    <div
      style={{
        backgroundColor: 'var(--bg-card)',
        border: alert ? `1px solid rgba(244, 63, 94, 0.3)` : `1px solid var(--border)`,
        borderRadius: '10px',
        padding: '20px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px' }}>
        <span
          style={{
            fontSize: '12px',
            fontWeight: 500,
            color: 'var(--text-muted)',
            letterSpacing: '0.01em',
          }}
        >
          {label}
        </span>
        {icon && (
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: `${color}12`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color,
            }}
          >
            {icon}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '6px' }}>
        <span
          style={{
            fontSize: '28px',
            fontWeight: 700,
            color: alert ? '#f43f5e' : 'var(--text-primary)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </span>
        {unit && (
          <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 500 }}>
            {unit}
          </span>
        )}
      </div>

      {subtitle && (
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px' }}>{subtitle}</div>
      )}

      {trend && trendValue && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {trend === 'up' ? (
            <TrendingUp size={12} style={{ color: trendPositive ? '#22c55e' : '#f43f5e' }} />
          ) : trend === 'down' ? (
            <TrendingDown size={12} style={{ color: trendPositive ? '#22c55e' : '#f43f5e' }} />
          ) : (
            <Minus size={12} style={{ color: 'var(--text-muted)' }} />
          )}
          <span
            style={{
              fontSize: '11px',
              color: trend === 'flat' ? 'var(--text-muted)' : trendPositive ? '#22c55e' : '#f43f5e',
              fontWeight: 500,
            }}
          >
            {trendValue}
          </span>
        </div>
      )}
    </div>
  );
}